/** Single source of truth for app paths (company-first navigation).
 *
 * Every navigation call site composes from here — the string '/companies/…'
 * should not appear inline anywhere else. Legacy '/assessments/:slug/*' URLs
 * are rewritten in App.tsx using `legacyAssessmentTarget`.
 */

export type ModuleKey = 'ai-assessment' | 'wafr' | 'ftr'

/** AI-assessment workflow stages that map 1:1 onto in-module subpaths. */
export type AiStage =
  | 'use-cases'
  | 'proposals'
  | 'analysis'
  /** Customer-facing deliverables: sign-off, the deck, every document. */
  | 'customer-submission'
  /** Everything AWS: pre-flight, the ACE opportunity, the funding request. */
  | 'aws-submission'
  | 'transcript-review'

export const paths = {
  home: () => '/',
  /** Public customer self-onboarding (no auth) — linked from the sign-in page. */
  newCustomer: () => '/newcustomer',
  help: () => '/help',
  /** Public help for the emailed intake questionnaire (no auth; static schema only). */
  questionnaireHelp: () => '/questionnaire-help',
  /** Public: request a password-reset email (customer accounts). */
  forgotPassword: () => '/forgot-password',
  /** Public: redeem an invite/reset link (the token rides in the #fragment). */
  setPassword: () => '/set-password',
  /** The customer portal — a customer's ONLY area (their own company). */
  portal: () => '/portal',
  portalProfile: () => '/portal/profile',
  /** The customer invites and manages colleagues at their company. */
  portalTeam: () => '/portal/team',
  /** The customer connects their AWS account and runs the scan. */
  portalAws: () => '/portal/aws',
  /** A customer's questionnaire for one started assessment. */
  portalModule: (module: ModuleKey) => `/portal/${module}`,
  company: (slug: string) => `/companies/${slug}`,
  /** Staff: who at the customer can sign in to the portal. */
  companyAccess: (slug: string) => `/companies/${slug}/access`,
  companyProfile: (slug: string) => `/companies/${slug}/profile`,
  /** Standalone AWS scan page (connection + read-only scrape), outside any module. */
  awsScan: (slug: string) => `/companies/${slug}/aws-scan`,
  /** Read-only detail table of the last scan's resources (region/account filters). */
  awsInventory: (slug: string) => `/companies/${slug}/aws-inventory`,
  /** Dedicated ACE-opportunity creation page. `returnTo` is the in-app path the
   *  page navigates back to with `ace_id=<new id>` appended on success. */
  aceNew: (slug: string, returnTo?: string) =>
    `/companies/${slug}/ace/new${returnTo ? `?return=${encodeURIComponent(returnTo)}` : ''}`,
  /** Company-scoped assistant (chat with company). `context` preselects the
   *  module scope in the composer dropdown. */
  assistant: (slug: string, context?: string) =>
    `/companies/${slug}/assistant${context ? `?context=${encodeURIComponent(context)}` : ''}`,
  research: (slug: string) => `/companies/${slug}/research`,
  moduleHome: (slug: string, module: ModuleKey) => `/companies/${slug}/${module}`,
  /** AI-assessment module home (= Intake) or one of its stages. */
  ai: (slug: string, stage?: AiStage) =>
    `/companies/${slug}/ai-assessment${stage ? `/${stage}` : ''}`,
  wafr: (slug: string) => `/companies/${slug}/wafr`,
  ftr: (slug: string) => `/companies/${slug}/ftr`,
}

/** Where a captured-field section actually lives in the UI — the one mapping
 *  used by every "go fix this field" affordance (pre-flight Fix buttons,
 *  Analysis suggestions). Backends emit the SECTION, never a URL.
 *
 *  Not every section renders on the intake page (`engagement_metadata` lives on
 *  Submission, `use_case_hypotheses` on its own stage); linking those at
 *  `?section=` would silently land on intake section 1.
 */
export function sectionDeepLink(slug: string, target: string): string {
  const section = target.split(/[.[]/)[0] ?? ''
  // `?tab=` matters: the Submission page's tab is component state, so linking
  // to the bare path from the Submission page itself (where the pre-flight
  // "Fix" buttons live) would navigate to the URL already showing and do
  // nothing at all.
  if (section === 'engagement_metadata') return `${paths.ai(slug, 'aws-submission')}?tab=engagement`
  if (section === 'use_case_hypotheses') return paths.ai(slug, 'use-cases')
  if (section === 'proposals') return paths.ai(slug, 'proposals')
  return `${paths.ai(slug)}?section=${encodeURIComponent(section)}`
}

/** Rewrite a legacy '/assessments/:slug/*' location to its new home.
 *  Query strings are preserved (`?section=`, `?prefill=&send=1`, …). */
export function legacyAssessmentTarget(slug: string, splat: string, search: string): string {
  const rest = splat.replace(/^\/+|\/+$/g, '')
  // Research review is company-scoped now.
  if (rest === 'research') return `${paths.research(slug)}${search}`
  const base = rest ? `${paths.ai(slug)}/${rest}` : paths.ai(slug)
  return `${base}${search}`
}
